import React, { useEffect, memo, useMemo } from 'react';
import { ConditionalSolidLayer } from '../../components/ui/ConditionalSolidLayer';
import type { BlurTint } from '../../shared/theme/blurParity';
import { View, Text, StyleSheet } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, interpolate, withSpring, Extrapolation, interpolateColor, useAnimatedProps } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../../shared/theme';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation } from '../../shared/i18n';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { blurIntensityForPlatform, FROSTED_BOTTOM_CHROME_RADIUS } from '../../shared/theme/sharedStyles';
import { getMenuFlowBottomOverlayPaddingBottom, IOS_MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM } from '../../navigation/mainTabs/androidFloatingTabBarLayout';

const BOTTOM_BLUR_MAX_INTENSITY = blurIntensityForPlatform(9);

/** Tab-bar clearance + horizontal inset. Used by: `DailyMenuFlowBottomMacroBar`, `OrderScreen` checkout overlay. */
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP = 10;
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM = IOS_MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM;
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL = 10;

function createDailyMenuFlowBottomMacroBarStyles(c: ThemeColors) {
  return StyleSheet.create({
    bottomMacroCard: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 99,
      flexDirection: 'row',
    },
    bottomMacroCardPadding: {
      paddingTop: MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP,
      paddingHorizontal: MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL,
      alignItems: 'center',
      justifyContent: 'center',
    },
    bottomMacroRow: {
      flexDirection: 'row',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      flex: 1,
    },
    bottomMacroItem: {
      alignItems: 'center',
    },
    bottomMacroItemCalories: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.calorieUltra,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 10,
      gap: 4,
    },
    bottomMacroValueProtein: {
      fontSize: 14,
      fontWeight: '700',
      color: c.protein,
    },
    bottomMacroValueCarbs: {
      fontSize: 14,
      fontWeight: '700',
      color: c.carbs,
    },
    bottomMacroValueFats: {
      fontSize: 14,
      fontWeight: '700',
      color: c.fats,
    },
    bottomMacroLabel: {
      fontSize: 12,
      color: c.textSecondary,
      marginTop: 2,
    },
    bottomMacroValueCalories: {
      fontSize: 18,
      fontWeight: '700',
      color: c.calorie,
    },
    bottomMacroLabelCalories: {
      fontSize: 12,
      fontWeight: '600',
      color: c.textSecondary,
      marginTop: 2,
    },
    bottomCompleteBadge: {
      height: 25,
      width: 25,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.success,
      borderRadius: 20,
    },
    savingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      marginBottom: 4,
    },
    savingText: {
      fontSize: 12,
      color: c.textSecondary,
    },
  });
}

interface DailyMenuFlowBottomMacroBarProps {
  macroTotals: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  isComplete: boolean;
  isVisible: boolean;
  useSolidBackground?: boolean;
  isSyncing?: boolean;
  /** Guest welcome flow: sit above `GuestMainBackFooter` instead of the tab bar. */
  bottomInsetOverride?: number;
  showMacroTotals?: boolean;
}

export const DailyMenuFlowBottomMacroBar = memo(({ macroTotals, isComplete, isVisible, useSolidBackground = false, isSyncing = false, bottomInsetOverride, showMacroTotals = true }: DailyMenuFlowBottomMacroBarProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const bottom_overlay_padding = bottomInsetOverride ?? getMenuFlowBottomOverlayPaddingBottom(insets.bottom);
  const appearance = useThemeStore(s => s.appearance);
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createDailyMenuFlowBottomMacroBarStyles(colors), [colors]);
  const bottom_bar_blur_tint: BlurTint = appearance === 'dark' ? 'systemChromeMaterialDark' : 'systemChromeMaterialLight';

  const transition = useSharedValue(0);
  const transitionComplete = useSharedValue(0);

  useEffect(() => {
    transition.value = withSpring(isVisible ? 1 : 0, {
      damping: 20,
      stiffness: 100,
    });
  }, [isVisible]);

  useEffect(() => {
    transitionComplete.value = withSpring(isComplete ? 1 : 0, {
      damping: 15,
      stiffness: 100,
    });
  }, [isComplete]);
  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: transition.value,
      transform: [{ translateY: interpolate(transition.value, [0, 1], [50, 0], Extrapolation.CLAMP) }],
    };
  });
  const completionStyle = useAnimatedStyle(() => ({
    opacity: transitionComplete.value,
    transform: [{ scale: interpolate(transitionComplete.value, [0, 0.5, 1], [0, 1.2, 1], Extrapolation.CLAMP) }],
  }));
  const backgroundColorStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(transitionComplete.value, [0, 1], [colors.primaryUltra, colors.primaryFade], 'RGB'),
    };
  });
  return (
    <Animated.View style={[styles.bottomMacroCard, styles.bottomMacroCardPadding, { paddingBottom: bottom_overlay_padding }, animatedStyle]}>
      <View
        style={[
          StyleSheet.absoluteFill,
          {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: colors.surface,
          },
        ]}
      />
      <Animated.View style={[StyleSheet.absoluteFill, backgroundColorStyle]} pointerEvents="none" />
      {isSyncing && (
        <View style={styles.savingRow}>
          <ButtonLoader size={18} color={colors.primary} />
          <Text style={styles.savingText}>{t('account.saving')}</Text>
        </View>
      )}
      {showMacroTotals ? (
        <View style={styles.bottomMacroRow}>
          <View style={styles.bottomMacroItemCalories}>
            <Text style={styles.bottomMacroValueCalories}>{macroTotals.calories}</Text>
            <Text style={styles.bottomMacroLabelCalories}>{t('menu.kcalLabel')}</Text>
          </View>

          <View style={styles.bottomMacroItem}>
            <Text style={styles.bottomMacroValueProtein}>{macroTotals.protein}g</Text>
            <Text style={styles.bottomMacroLabel}>{t('dashboard.protein')}</Text>
          </View>

          <View style={styles.bottomMacroItem}>
            <Text style={styles.bottomMacroValueCarbs}>{macroTotals.carbs}g</Text>
            <Text style={styles.bottomMacroLabel}>{t('dashboard.carbs')}</Text>
          </View>

          <View style={styles.bottomMacroItem}>
            <Text style={styles.bottomMacroValueFats}>{macroTotals.fats}g</Text>
            <Text style={styles.bottomMacroLabel}>{t('dashboard.fat')}</Text>
          </View>
        </View>
      ) : null}
      <Animated.View style={[styles.bottomCompleteBadge, completionStyle]}>
        <SvgIcon name="check" size={16} color={colors.white} />
      </Animated.View>
    </Animated.View>
  );
});
